import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Copy, Pencil, Trash2, ExternalLink, FileUp, Loader2, ShieldAlert, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useCachedList, primeCache } from "@/lib/cache";
import { useEditGate } from "@/components/EditGate";
import ComponentSelect from "@/components/ComponentSelect";
import TierFilter, { TierBadge } from "@/components/TierFilter";
import { PasswordPrompt, ConfirmDelete } from "@/components/DeleteFlow";
import { downloadTemplate, downloadImportReport, importToastMessage } from "@/lib/importReport";
import { TagPicker } from "@/components/tags/TagPicker";
import { matchesTags } from "@/components/tags/TagBadges";
import { InlineTags } from "@/components/tags/InlineTags";

const DB_TIERS = ["L1", "L1.5", "L2"];

export default function IngredientDatabasePage() {
  const [ingredientsData, refreshIngredients] = useCachedList("ingredients");
  const [recipesData, refreshRecipes] = useCachedList("recipes");
  const ingredients = ingredientsData || [];
  const recipes = recipesData || [];
  const loading = !recipesData;
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState([]);
  const [tierFilter, setTierFilter] = useState(() => new Set(DB_TIERS));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingL1, setEditingL1] = useState(null);
  const [importing, setImporting] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pwOpen, setPwOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { requireEdit } = useEditGate();
  const fileRef = useRef(null);

  const load = () => { refreshIngredients(); refreshRecipes(); };

  const rows = useMemo(() => {
    let r = recipes.filter((x) => DB_TIERS.includes(x.level) && matchesTags(x, tagFilter));
    if (tierFilter.size > 0) r = r.filter((x) => tierFilter.has(x.level));
    else r = [];
    if (query.trim()) r = r.filter((x) => `${x.name} ${x.sku || ""} ${x.level} ${x.parent_l0 || ""}`.toLowerCase().includes(query.toLowerCase()));
    return r.sort((a, b) => a.level.localeCompare(b.level) || a.name.localeCompare(b.name));
  }, [recipes, query, tierFilter, tagFilter]);

  const copyAll = () => {
    const header = ["SKU", "Name", "Tier", "Raw Material (L0)", "Wastage %", "AP Price/g", "EP Price/g", "UOM", "Used In", "Tags"].join("\t");
    const body = rows.map((r) => [r.sku || "", r.name, r.level, r.parent_l0 || "—", r.wastage_pct.toFixed(1), r.ap_unit_cost.toFixed(4), r.unit_cost.toFixed(4), r.output_unit, r.usage_count ?? 0, (r.tags || []).join(", ")].join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${body}`);
    toast.success("Copied to clipboard (TSV)");
  };

  const remove = (r) => requireEdit(async () => {
    try { await api.delete(`/recipes/${r.id}`); toast.success(`Deleted ${r.name}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  });

  const openDelete = () => {
    if (rows.length === 0) return toast.error("Nothing to delete.");
    setPwOpen(true);
  };

  const onPwVerified = () => {
    setDeleteMode(true);
    setSelectedIds(new Set());
    setPwOpen(false);
    toast.info("Delete mode on — tick the items you want to remove, then click 'Delete selected'.");
  };

  const exitDeleteMode = () => { setDeleteMode(false); setSelectedIds(new Set()); };

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleSelectAll = () => {
    const visibleIds = rows.map((r) => r.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((s) => {
      const n = new Set(s);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const askDelete = () => {
    if (selectedIds.size === 0) return toast.error("No items selected.");
    setConfirmOpen(true);
  };

  const performDelete = async (password) => {
    try {
      const { data } = await api.post("/recipes/bulk-delete", { password, ids: Array.from(selectedIds) });
      toast.success(`Deleted ${data.deleted} item${data.deleted === 1 ? "" : "s"}`);
      await refreshRecipes();
      setConfirmOpen(false);
      exitDeleteMode();
    } catch (e) { toast.error(apiError(e)); }
  };

  const importFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (importing) { toast.error("A bulk upload is already in progress — please wait"); return; }
    requireEdit(async () => {
      setImporting(true);
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/recipes/import-l1", fd, { headers: { "Content-Type": "multipart/form-data" } });
        primeCache("recipes", data.rows);
        toast.success(`Import done: ${importToastMessage(data)}`, { duration: 10000 });
        if (data.errors?.length) data.errors.slice(0, 8).forEach((m) => toast.error(m, { duration: 10000 }));
        downloadImportReport("l1_ingredients", data);
        refreshRecipes();
      } catch (err) { toast.error(apiError(err)); }
      finally { setImporting(false); }
    });
  };

  return (
    <div className="min-h-screen" data-testid="ingredient-database-page">
      <div className="sticky top-0 z-20 border-b border-zinc-200 bg-white/95 px-6 py-4 backdrop-blur md:px-8">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Ingredient Database</h1>
            <p className="mt-1 text-sm text-zinc-500">Unified view of all prepped (L1) and semi-finished (L1.5 / L2) ingredients with AP → EP pricing.</p>
          </div>
          <div className="flex flex-wrap gap-2 no-print">
            <input ref={fileRef} data-testid="import-l1-file-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={importFile} />
            <button data-testid="download-l1-template-button" onClick={() => downloadTemplate("l1_ingredients_template.csv",
              ["Name", "SKU (optional)", "Parent L0", "Input Qty (g)", "Wastage %"],
              [["Sliced Onion", "", "Onion", 1000, 10], ["Cleaned Chicken Cubes", "", "Chicken Breast", 1000, 5]])}
              disabled={deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
              <FileUp className="h-4 w-4 rotate-180" strokeWidth={1.5} /> Template
            </button>
            <button data-testid="import-l1-button" onClick={() => fileRef.current?.click()} disabled={importing || deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <FileUp className="h-4 w-4" strokeWidth={1.5} />}
              {importing ? "Importing…" : "Import L1 items"}
            </button>
            <button data-testid="copy-database-button" onClick={copyAll} disabled={deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
              <Copy className="h-4 w-4" strokeWidth={1.5} /> Copy grid
            </button>
            {!deleteMode ? (
              <button data-testid="open-db-delete-mode-button" onClick={openDelete} disabled={importing}
                className="inline-flex items-center gap-1.5 rounded-sm border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-50">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete ingredients
              </button>
            ) : (
              <button data-testid="cancel-db-delete-mode-button" onClick={exitDeleteMode}
                className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">
                <X className="h-4 w-4" strokeWidth={1.5} /> Cancel delete
              </button>
            )}
            <button data-testid="new-l1-button" onClick={() => requireEdit(() => { setEditingL1(null); setDialogOpen(true); })} disabled={deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New L1 item
            </button>
          </div>
        </div>

        <p className="mb-3 text-xs text-zinc-500">
          Filter by tier and search across name, SKU or parent L0. L1 items can be bulk-imported via Excel/CSV — the file needs columns: Name, SKU (optional), Parent L0, Input Qty, Wastage %. Existing rows update on name match; duplicates within a file are skipped.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 max-w-md flex-1 basis-64 items-center gap-2 rounded-sm border border-zinc-300 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
            <input data-testid="database-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search ingredients or SKU…"
              className="w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0" />
          </div>
          <TierFilter tiers={DB_TIERS} selected={tierFilter} onChange={setTierFilter} testidPrefix="db-tier-filter" />
          <TagPicker testid="database-tag-filter" filter value={tagFilter} onChange={setTagFilter} />
          <span data-testid="database-filter-result-count" className="text-xs text-zinc-500">{rows.length} items</span>
          {deleteMode && (
            <div className="flex items-center gap-2 rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="db-delete-mode-banner">
              <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
              <span>{selectedIds.size} selected</span>
              <button data-testid="db-bulk-delete-selected-button" onClick={askDelete} disabled={selectedIds.size === 0}
                className="rounded-sm bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                Delete selected
              </button>
            </div>
          )}
        </div>
      </div>

      {importing && (
        <div className="border-b border-blue-200 bg-blue-50 px-6 py-3 md:px-8" data-testid="l1-import-progress-banner">
          <div className="flex items-center gap-3 text-sm text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            <span className="font-medium">Processing your L1 file…</span>
            <span className="text-blue-700/80">Please wait until this finishes. Do not upload another file.</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
            <div className="h-full w-1/3 animate-[progress_1.4s_ease-in-out_infinite] rounded-full bg-blue-500" />
          </div>
        </div>
      )}

      <div className="px-6 pb-6 md:px-8">
        <div data-testid="database-table-scroll" tabIndex={0} role="region" aria-label="Ingredient database table" className={`overflow-x-auto border border-zinc-300 ${importing ? "pointer-events-none opacity-60" : ""}`}>
          <table data-testid="database-table" className="w-max min-w-full border-collapse text-left [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
            <thead>
              <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
                {deleteMode && (
                  <th className="border-b border-zinc-300 px-3 py-2 w-8">
                    <input type="checkbox" data-testid="db-select-all-checkbox" checked={rows.length > 0 && rows.every((r) => selectedIds.has(r.id))} onChange={toggleSelectAll} />
                  </th>
                )}
                <th className="border-b border-zinc-300 px-3 py-2 w-28">SKU</th>
                <th className="min-w-64 border-b border-l border-zinc-300 px-3 py-2">Name</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 w-20">Tier</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 w-44">Raw Material (L0)</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Wastage %</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">AP Price</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">EP Price</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 w-16">UOM</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-center w-24" title="Number of downstream recipes referencing this ingredient">Used In</th>
                {!deleteMode && <th className="border-b border-l border-zinc-300 px-3 py-2 w-20"></th>}
                <th data-testid="database-tags-header" className="border-b border-l border-zinc-300 px-3 py-2">Tags</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="px-3 py-8 text-center text-sm text-zinc-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td data-testid="database-filter-empty" colSpan={11} className="px-3 py-8 text-center text-sm text-zinc-400">{tierFilter.size === 0 ? "No tier selected — pick at least one." : "No matching items."}</td></tr>}
              {rows.map((r) => {
                const isSelected = selectedIds.has(r.id);
                return (
                  <tr key={r.id} className={`hover:bg-zinc-50 text-[13px] ${isSelected ? "bg-rose-50/50" : ""}`} data-testid={`database-row-${r.id}`}>
                    {deleteMode && (
                      <td className="border-b border-zinc-200 px-3 py-2 text-center">
                        <input type="checkbox" data-testid={`db-select-row-checkbox-${r.id}`} checked={isSelected} onChange={() => toggleSelect(r.id)} />
                      </td>
                    )}
                    <td className="border-b border-zinc-200 px-3 py-2 font-mono text-[12px] text-zinc-600" data-testid={`database-sku-${r.id}`}>{r.sku || "—"}</td>
                    <td data-testid={`database-name-${r.id}`} className="border-b border-l border-zinc-200 px-3 py-2 font-medium">
                      {r.level === "L1" ? r.name : (
                        <Link data-testid={`database-recipe-link-${r.id}`} to={`/recipes/${r.id}`} className="inline-flex items-center gap-1 text-zinc-900 hover:text-blue-600">
                          {r.name} <ExternalLink className="h-3 w-3 text-zinc-400" strokeWidth={1.5} />
                        </Link>
                      )}
                    </td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2"><TierBadge tier={r.level} /></td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-zinc-600">{r.level === "L1" ? (r.parent_l0 || "—") : <span className="text-zinc-400">from recipe</span>}</td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono text-amber-600">{r.wastage_pct.toFixed(1)}%</td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">₹{r.ap_unit_cost.toFixed(4)}<span className="text-zinc-400">/{r.output_unit}</span></td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono font-medium">₹{r.unit_cost.toFixed(4)}<span className="font-normal text-zinc-400">/{r.output_unit}</span></td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-zinc-500">{r.output_unit}</td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-center" data-testid={`database-usage-${r.id}`}>
                      {(r.usage_count ?? 0) > 0 ? (
                        <span className="inline-flex min-w-[24px] items-center justify-center rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800" title={`Referenced by ${r.usage_count} downstream recipe${r.usage_count === 1 ? "" : "s"}`}>{r.usage_count}</span>
                      ) : (
                        <span className="text-[11px] text-zinc-400">0</span>
                      )}
                    </td>
                    {!deleteMode && (
                      <td className="border-b border-l border-zinc-200 px-2 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-item-${r.id}`} onClick={() => requireEdit(() => { setEditingL1(r); setDialogOpen(true); })}
                            className="p-1 text-zinc-400 transition-colors hover:text-blue-600" title={r.level === "L1" ? "Edit L1" : "Edit wastage and tags"}>
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="border-b border-l border-zinc-200 px-3 py-2">
                      <InlineTags item={r} testid={`database-tags-${r.id}`} disabled={deleteMode || importing} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PasswordPrompt open={pwOpen} onClose={() => setPwOpen(false)} onVerified={onPwVerified}
        description="Enter the admin password to enable deletion. Deleting an L1/L1.5/L2 item will break any downstream recipe (L1.5/L2/L3) that references it — you'll need to re-tag those recipes." />
      <ConfirmDelete
        open={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        count={selectedIds.size}
        items={rows.filter((r) => selectedIds.has(r.id))}
        itemLabel="ingredient"
        warning="Any downstream recipe (L1.5/L2/L3) tagged to a deleted item will show '(deleted)' in its component list until you re-tag it. Items flagged as 'Used In X' are used by X other recipes."
      />

      <L1Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} ingredients={ingredients} editing={editingL1} onSaved={load} />
    </div>
  );
}

function L1Dialog({ open, onClose, ingredients, editing, onSaved }) {
  const { requireEdit } = useEditGate();
  const isCooked = editing && (editing.level === "L1.5" || editing.level === "L2");
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [tags, setTags] = useState([]);
  const [l0Id, setL0Id] = useState("");
  const [inputQty, setInputQty] = useState(1000);
  const [wastage, setWastage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setSku(editing?.sku || "");
      setTags(editing?.tags || []);
      const comp = editing?.components?.[0];
      if (editing?.level === "L1") {
        const parentExists = comp && ingredients.some((i) => i.id === comp.ref_id);
        setL0Id(parentExists ? comp.ref_id : "");
        if (editing && comp && !parentExists) toast.warning("The original L0 raw material no longer exists — select a new one to re-tag this item.");
        setInputQty(comp?.qty ?? 1000);
        setWastage(editing.wastage_pct != null ? String(editing.wastage_pct) : "");
      } else if (isCooked) {
        setWastage(editing.wastage_pct > 0 ? String(editing.wastage_pct) : "");
      } else {
        setL0Id(""); setInputQty(1000); setWastage("");
      }
    }
  }, [open, editing, ingredients, isCooked]);

  const options = ingredients.map((i) => ({ value: i.id, label: i.name, level: "L0", unit: i.base_unit, unit_cost: i.cost_per_base_unit }));
  const l0 = ingredients.find((i) => i.id === l0Id);
  const w = Number(wastage) || 0;
  const inQty = Number(inputQty) || 0;
  const outQty = inQty * (1 - w / 100);
  const ap = isCooked ? (editing.total_cost / (editing.output_qty || 1)) : (l0?.cost_per_base_unit || 0);
  const ep = isCooked
    ? (w < 100 ? ap / (1 - w / 100) : 0)
    : (w < 100 && outQty > 0 ? (ap * inQty) / outQty : 0);
  const unitLabel = isCooked ? (editing.output_unit || "g") : (l0?.base_unit || "g");

  const save = () => requireEdit(async (password) => {
    if (isCooked) {
      if (w < 0 || w >= 100) return toast.error("Wastage % must be between 0 and 99.9");
      setBusy(true);
      try {
        await api.patch(`/recipes/${editing.id}/wastage`, { wastage_pct: w, tags, password });
        toast.success("Item updated");
        onSaved(); onClose();
      } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
      return;
    }
    if (!name.trim()) return toast.error("Name is required");
    if (!l0Id) return toast.error("Tag an existing L0 raw material. If it doesn't exist, create it in L0 Raw Materials first.");
    if (inQty <= 0) return toast.error("Batch input quantity must be > 0");
    if (w < 0 || w >= 100) return toast.error("Wastage % must be between 0 and 99.9");
    setBusy(true);
    try {
      const body = {
        name: name.trim(), sku: sku.trim(), level: "L1",
        output_qty: editing && inQty === editing.components?.[0]?.qty && w === editing.wastage_pct ? editing.output_qty : outQty,
        output_unit: l0?.base_unit || "g", portion_qty: editing?.portion_qty || 0,
        components: [{ ref_type: "ingredient", ref_id: l0Id, qty: inQty }], tags, password,
      };
      if (editing?.id) await api.put(`/recipes/${editing.id}`, body);
      else await api.post("/recipes", body);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, true);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-[min(32rem,calc(100vw-2rem))] overflow-y-auto rounded-sm" data-testid="l1-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">
            {editing ? `Edit ${editing.level === "L1" ? "L1 Prepped Ingredient" : `${editing.level} Ingredient`}` : "New L1 Prepped Ingredient"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isCooked ? "Edit wastage and tags for this cooked ingredient." : "Edit the prepped ingredient, its parent raw material and optional tags."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Name</label>
              <input data-testid="l1-name-input" value={name} onChange={(e) => setName(e.target.value)} disabled={isCooked} placeholder="e.g., Peeled Potato"
                className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-zinc-50 disabled:text-zinc-500" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">SKU</label>
              <input data-testid="l1-sku-input" value={sku} onChange={(e) => setSku(e.target.value)} disabled={isCooked} placeholder="auto"
                className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-zinc-50 disabled:text-zinc-500" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase text-zinc-600">Tags <span className="font-normal normal-case text-zinc-400">(optional)</span></div>
            <TagPicker testid="l1-tags" value={tags} onChange={setTags} disabled={busy} />
          </div>
          {!isCooked && (
            <>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Parent L0 Raw Material</label>
                <div className="mt-1">
                  <ComponentSelect testid="l1-l0-select" options={options} value={l0Id} onChange={setL0Id} />
                </div>
                {options.length === 0 && <p className="mt-1 text-xs text-rose-600">No L0 raw materials exist — create one in L0 Raw Materials first.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Batch Input Qty ({l0?.base_unit || "g"})</label>
                  <input data-testid="l1-input-qty" type="number" step="any" value={inputQty} onChange={(e) => setInputQty(e.target.value)}
                    className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Wastage %</label>
                  <input data-testid="l1-wastage-input" type="number" step="any" value={wastage} onChange={(e) => setWastage(e.target.value)} placeholder="e.g., 10"
                    className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
                </div>
              </div>
            </>
          )}
          {isCooked && (
            <>
              <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600" data-testid="cooked-locked-hint">
                To change components, output or yield, open the recipe from the recipe directory.
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Wastage %</label>
                <input data-testid="l1-wastage-input" type="number" step="any" value={wastage} onChange={(e) => setWastage(e.target.value)} placeholder="0"
                  className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
                <p className="mt-1 text-[11px] text-zinc-500">Leave blank for zero wastage. This inflates the EP price used by downstream recipes.</p>
              </div>
            </>
          )}
          <div className="grid grid-cols-3 gap-px border border-zinc-300 bg-zinc-300">
            <div className="bg-zinc-50 px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-zinc-500">{isCooked ? "Batch Output" : "Output"}</div>
              <div data-testid="l1-output" className="mt-0.5 font-mono text-sm font-medium">
                {(isCooked ? (editing.output_qty || 0) : outQty).toFixed(1)} {unitLabel}
              </div>
            </div>
            <div className="bg-zinc-50 px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-zinc-500">AP Price</div>
              <div data-testid="l1-ap-price" className="mt-0.5 font-mono text-sm font-medium">₹{ap.toFixed(4)}/{unitLabel}</div>
            </div>
            <div className="bg-zinc-50 px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-zinc-500">EP Price</div>
              <div data-testid="l1-ep-price" className="mt-0.5 font-mono text-sm font-medium text-blue-700">₹{ep.toFixed(4)}/{unitLabel}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button data-testid="cancel-l1-button" disabled={busy} onClick={onClose} className="rounded-sm border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">Cancel</button>
            <button data-testid="save-l1-button" onClick={save} disabled={busy}
              className="rounded-sm bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Saving…" : (isCooked ? "Save changes" : "Save L1 item")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
